import QRCode from 'qrcode';

interface GenerateClinicQrOptions {
    value: string;
    gameName: string;
    clinicName: string;
}

const CANVAS_SIZE = 420;
const QR_SIZE = 260;
const HORIZONTAL_PADDING = 24;

const GAME_FONT = 'bold 20px Arial';
const CLINIC_FONT = '16px Arial';

const GAME_LINE_HEIGHT = 26;
const CLINIC_LINE_HEIGHT = 22;

const TOP_PADDING = 24;
const GAP_BEFORE_QR = 18;
const GAP_AFTER_QR = 20;

const wrapText = (
    ctx: CanvasRenderingContext2D,
    text: string,
    maxWidth: number
): string[] => {
    const words = text.split(/\s+/).filter(Boolean);

    if (words.length === 0) {
        return [''];
    }

    const lines: string[] = [];
    let currentLine = words[0];

    for (let index = 1; index < words.length; index += 1) {
        const candidate = `${currentLine} ${words[index]}`;

        if (ctx.measureText(candidate).width <= maxWidth) {
            currentLine = candidate;
        } else {
            lines.push(currentLine);
            currentLine = words[index];
        }
    }

    lines.push(currentLine);

    return lines;
};

export const safeQrFileName = (text: string): string =>
    text
        .replace(/[^a-z0-9]+/gi, '_')
        .replace(/^_+|_+$/g, '');

export const generateClinicQrCanvas = async ({
    value,
    gameName,
    clinicName,
}: GenerateClinicQrOptions): Promise<HTMLCanvasElement> => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) {
        throw new Error('Unable to create QR canvas');
    }

    const scaleFactor = window.devicePixelRatio || 1;

    // Fixed square image.
    canvas.width = CANVAS_SIZE * scaleFactor;
    canvas.height = CANVAS_SIZE * scaleFactor;

    context.scale(scaleFactor, scaleFactor);

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    const textWidth = CANVAS_SIZE - HORIZONTAL_PADDING * 2;

    context.font = GAME_FONT;
    const gameLines = wrapText(context, gameName, textWidth).slice(0, 2);

    context.font = CLINIC_FONT;
    const clinicLines = wrapText(context, clinicName, textWidth).slice(0, 2);

    context.textAlign = 'center';
    context.textBaseline = 'top';

    // Draw game name at the top.
    let cursorY = TOP_PADDING;

    context.font = GAME_FONT;
    context.fillStyle = '#000000';

    gameLines.forEach((line) => {
        context.fillText(line, CANVAS_SIZE / 2, cursorY);
        cursorY += GAME_LINE_HEIGHT;
    });

    cursorY += GAP_BEFORE_QR;

    // Generate QR code.
    const qrCanvas = document.createElement('canvas');

    await QRCode.toCanvas(qrCanvas, value, {
        width: QR_SIZE,
        margin: 0,
        errorCorrectionLevel: 'M',
        color: {
            dark: '#000000',
            light: '#ffffff',
        },
    });

    // Centre QR horizontally.
    const qrX = (CANVAS_SIZE - QR_SIZE) / 2;

    context.drawImage(
        qrCanvas,
        qrX,
        cursorY,
        QR_SIZE,
        QR_SIZE
    );

    cursorY += QR_SIZE + GAP_AFTER_QR;

    // Draw clinic name underneath.
    context.font = CLINIC_FONT;
    context.fillStyle = '#333333';

    clinicLines.forEach((line) => {
        context.fillText(line, CANVAS_SIZE / 2, cursorY);
        cursorY += CLINIC_LINE_HEIGHT;
    });

    return canvas;
};

export const generateClinicQrBlob = async (
    options: GenerateClinicQrOptions
): Promise<Blob> => {
    const canvas = await generateClinicQrCanvas(options);

    return new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
                if (!blob) {
                    reject(new Error('Unable to generate QR image'));
                    return;
                }

                resolve(blob);
            },
            'image/png',
            1
        );
    });
};